import React from 'react'
import {
  Autocomplete,
  Box,
  Button,
  Fade,
  Tooltip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import AdminInternalNav from './AdminInternalNav'

const colors = {
  ink: '#06185f',
  muted: '#4c5d86',
  blue: '#0059f6',
  line: '#d9e3f4',
}

const statusButtonBase = {
  minHeight: 36,
  px: 2.4,
  borderRadius: '8px',
  fontSize: 12,
  fontWeight: 900,
  textTransform: 'none',
  boxShadow: 'none',
}

const statusColors = {
  VENTA: { solid: '#96c87c', text: '#2f8f3a', bg: 'rgba(150, 200, 124, 0.14)' },
  BAJA: { solid: '#fd8d82', text: '#e34d43', bg: 'rgba(253, 141, 130, 0.14)' },
  FUTURO: { solid: '#b1b5c2', text: '#7d8495', bg: 'rgba(177, 181, 194, 0.16)' },
}

const statusKeys = ['VENTA', 'BAJA', 'FUTURO']

const allStatusFilters = statusKeys.reduce((acc, key) => ({ ...acc, [key]: true }), {})

/**
 * Props:
 * - filters: {VENTA: bool, BAJA: bool, FUTURO: bool}
 * - onChangeFilters(obj)
 * - onBackHome()
 * - onBackNoAsignados()
 */
export default function ConsultasCerradasHead({
  filters,
  onChangeFilters,
  vendedorFilter,
  onChangeVendedorFilter,
  vendedores = [],
  onBackHome,
  onBackNoAsignados,
  onOpenClientes,
  isFullScreen,
  onToggleFullScreen,
}) {
  const toggle = (key) => {
    const activeCount = statusKeys.filter(estado => filters?.[estado]).length

    if (activeCount === statusKeys.length) {
      onChangeFilters?.(statusKeys.reduce((acc, estado) => ({ ...acc, [estado]: estado === key }), {}))
      return
    }

    const next = { ...allStatusFilters, ...filters, [key]: !filters?.[key] }
    const nextActiveCount = statusKeys.filter(estado => next[estado]).length

    if (nextActiveCount === 0 || nextActiveCount === statusKeys.length) {
      onChangeFilters?.(allStatusFilters)
      return
    }

    onChangeFilters?.(next)
  }

  const VendorFilter = (
    <Autocomplete
      options={vendedores}
      value={
        vendedores.find(v => v.id === vendedorFilter) ||
        (vendedorFilter === 'todos' ? { id: 'todos', nombre: '' } : null)
      }
      onChange={(event, newValue) => {
        onChangeVendedorFilter?.(newValue ? newValue.id : 'todos');
      }}
      getOptionLabel={(option) => option.nombre}
      size="small"
      sx={{
        width: { xs: '100%', sm: 220 },
        '& .MuiOutlinedInput-root': {
          height: 38,
          borderRadius: '8px',
          color: colors.ink,
          fontSize: 12,
          fontWeight: 700,
          backgroundColor: 'rgba(255,255,255,0.92)',
          boxShadow: '0 10px 22px rgba(19, 39, 84, 0.08)',
          '& fieldset': { borderColor: colors.line },
          '&:hover fieldset': { borderColor: 'rgba(0,89,246,0.35)' },
          '&.Mui-focused fieldset': { borderColor: colors.blue },
        },
        '& .MuiInputLabel-root': {
          color: colors.muted,
          fontSize: 13,
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Vendedor"
          placeholder="Buscar vendedor..."
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          {option.nombre}
        </Box>
      )}
      isOptionEqualToValue={(option, value) => option.id === value.id}
    />
  )

  const PrimaryRow = (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', md: 'center' }}
      spacing={2}
      sx={{ width: '100%' }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            color: colors.ink,
            fontSize: 26,
            fontWeight: 900,
            letterSpacing: '-0.7px',
            lineHeight: 1.1,
          }}
        >
          Consultas cerradas
        </Typography>
        <Typography sx={{ mt: 0.5, color: colors.muted, fontSize: 12, fontWeight: 500 }}>
          Consultá ventas, bajas y oportunidades futuras ya cerradas por el equipo.
        </Typography>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
        spacing={1}
        flexWrap="wrap"
        useFlexGap
      >
        {!isFullScreen && (
          <AdminInternalNav
            active="cerradas"
            onHome={onBackHome}
            onNoAsignadas={onBackNoAsignados}
            onCerradas={() => {}}
            onClientes={onOpenClientes}
          />
        )}
      </Stack>
    </Stack>
  )

  const SecondaryRow = (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      alignItems={{ xs: 'stretch', md: 'center' }}
      justifyContent="space-between"
      spacing={1.5}
      sx={{ width: '100%' }}
    >
      {VendorFilter}

      <Stack
        direction="row"
        alignItems="center"
        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
        spacing={1}
        flexWrap="wrap"
        useFlexGap
      >
        <Typography sx={{ color: colors.muted, fontSize: 12, fontWeight: 700, mr: 0.5 }}>
          Filtros por estado
        </Typography>

        {statusKeys.map(estado => (
          <Button
            key={estado}
            variant="outlined"
            onClick={() => toggle(estado)}
            sx={{
              ...statusButtonBase,
              minWidth: 94,
              color: filters[estado] ? statusColors[estado].text : colors.muted,
              borderColor: filters[estado] ? statusColors[estado].solid : colors.line,
              backgroundColor: filters[estado] ? statusColors[estado].bg : 'rgba(255,255,255,0.88)',
              '&:hover': {
                borderColor: statusColors[estado].solid,
                backgroundColor: statusColors[estado].bg,
                boxShadow: 'none',
              },
            }}
          >
            {estado}
          </Button>
        ))}

        <Tooltip title={isFullScreen ? "Salir de vista completa" : "Vista completa"}>
          <IconButton
            onClick={() => onToggleFullScreen && onToggleFullScreen(!isFullScreen)}
            sx={{
              width: 38,
              height: 38,
              color: colors.blue,
              border: `1px solid ${colors.line}`,
              borderRadius: '8px',
              backgroundColor: 'rgba(255,255,255,0.88)',
              '&:hover': { backgroundColor: '#edf4ff' },
            }}
          >
            {isFullScreen ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  )

  const ToolbarContent = (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {PrimaryRow}
      {SecondaryRow}
    </Stack>
  )

  const FullscreenToolbarContent = (
    <Stack spacing={1} sx={{ width: '100%' }}>
      {SecondaryRow}
    </Stack>
  )

  if (isFullScreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: '60px',
          left: 0,
          width: '100%',
          zIndex: 1050,
          height: '96px',
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
          '&:hover': { pointerEvents: 'auto' },
          '&:hover .MuiPaper-root': {
            transform: 'translateY(0)',
            opacity: 1,
          }
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '95%',
            mt: 1,
            px: 2,
            py: 1.2,
            borderRadius: '10px',
            bgcolor: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            display: 'flex',
            alignItems: 'center',
            transform: 'translateY(-120%)',
            opacity: 0,
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s',
            border: `1px solid ${colors.line}`,
            boxShadow: '0 18px 38px rgba(19, 39, 84, 0.14)',
            pointerEvents: 'auto'
          }}
        >
          {FullscreenToolbarContent}
        </Paper>

        <Fade in={true} timeout={1000}>
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '220px',
            height: '6px',
            bgcolor: colors.blue,
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            opacity: 0.8,
            pointerEvents: 'auto',
            cursor: 'pointer',
            zIndex: 1290,
            boxShadow: '0 2px 4px rgba(0,0,0,0.16)'
          }} />
        </Fade>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2.2 }}>
      {ToolbarContent}
    </Box>
  )
}
